import {type EmailSyncDetails, type MaterialStorage} from '@domain/material-storage'
import {dataTestId} from '@hconnect/uikit'
import {DeleteOutlined} from '@mui/icons-material'
import {Stack, TextField} from '@mui/material'
import {useForm, Controller} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {DeleteButton, NumberInput} from '@settings/modules/common/components'
import {useConfirmDialog} from '@settings/modules/common/providers'
import {
  minValidator,
  requiredValidator,
  submitOnBlurAndEnterProps
} from '@settings/modules/common/utils'
import {useUrlParam} from '@settings/modules/routing'
import {
  useAddEmailParserConnection,
  useDeleteDataConnection,
  useEditEmailParserConnection
} from '@settings/modules/storages/hooks'

interface EmailParserConnectionFormProps {
  storage: MaterialStorage
  isReadOnly: boolean
  emailParserConnection: EmailSyncDetails
  setEmailParserConnection: (value: EmailSyncDetails | null) => void
}

export const EmailParserConnectionForm: React.FC<EmailParserConnectionFormProps> = ({
  storage,
  isReadOnly,
  emailParserConnection,
  setEmailParserConnection
}) => {
  const {t} = useTranslation()
  const plantCode = useUrlParam('plantCode')
  const {openDialog} = useConfirmDialog()

  const {mutate: addEmailParserConnection, isPending: isConnectionAdding} =
    useAddEmailParserConnection()
  const {mutate: editEmailParserConnection, isPending: isConnectionEditing} =
    useEditEmailParserConnection()
  const {mutate: deleteDataConnection, isPending: isConnectionDeleting} = useDeleteDataConnection()

  const isNewEmailParserConnection = !storage.emailSyncDetails

  const isFormDisabled =
    isReadOnly || isConnectionAdding || isConnectionDeleting || isConnectionEditing

  const {
    handleSubmit,
    control,
    reset,
    formState: {isDirty, dirtyFields}
  } = useForm<EmailSyncDetails>({
    mode: 'onChange',
    shouldFocusError: false,
    defaultValues: emailParserConnection
  })

  const submit = handleSubmit((values) => {
    if (!isDirty) return

    if (isNewEmailParserConnection) {
      return addEmailParserConnection(
        {plantCode, storageId: storage.id, dto: values},
        {onSuccess: () => setEmailParserConnection(values)}
      )
    }

    Object.keys(dirtyFields).forEach((key) => {
      editEmailParserConnection(
        {
          plantCode,
          storageId: storage.id,
          dto: values,
          key: key as keyof EmailSyncDetails
        },
        {
          onSuccess: () => {
            reset(values)
            setEmailParserConnection(values)
          }
        }
      )
    })
  })

  const deleteConnection = () => {
    if (isNewEmailParserConnection) {
      return setEmailParserConnection(null)
    }

    openDialog({
      title: t('storagesSettings.deleteEmailParserConnection'),
      description: t('storagesSettings.deleteEmailParserConnectionDescription'),
      mainAction: {
        text: t('common.delete'),
        color: 'error',
        icon: <DeleteOutlined />,
        onAction: () => {
          deleteDataConnection({plantCode, storageId: storage.id, type: 'email-parser'})
          setEmailParserConnection(null)
        }
      },
      testId: 'delete_email_parser_connection_dialog'
    })
  }

  return (
    <Stack
      spacing={2}
      direction="row"
      alignItems="center"
      {...dataTestId('email_parser_connection_form')}
    >
      <Controller
        control={control}
        name="emailColumnName"
        rules={requiredValidator(t)}
        render={({field: {ref, value, onChange}, fieldState: {error}}) => (
          <TextField
            sx={{width: ({spacing}) => spacing(27)}}
            variant="outlined"
            label={t('storagesSettings.emailColumnName')}
            InputLabelProps={{shrink: true}}
            inputRef={ref}
            value={value}
            onChange={onChange}
            helperText={error?.message}
            error={Boolean(error?.message)}
            disabled={isFormDisabled}
            {...submitOnBlurAndEnterProps(submit)}
            {...dataTestId('email_column_name_input')}
          />
        )}
      />
      <Controller
        control={control}
        name="tonneConversionFactor"
        rules={{...requiredValidator(t), ...minValidator(t, 0)}}
        render={({field: {ref, value, onChange}, fieldState: {error}}) => (
          <NumberInput
            sx={{width: ({spacing}) => spacing(27)}}
            label={t('storagesSettings.tonneConversionFactor')}
            inputRef={ref}
            value={value}
            min={0}
            onChange={onChange}
            error={error?.message}
            disabled={isFormDisabled}
            dataTestId="tonne_conversion_factor_input"
            {...submitOnBlurAndEnterProps(submit)}
          />
        )}
      />
      <div>
        <DeleteButton
          onClick={deleteConnection}
          disabled={isFormDisabled}
          {...dataTestId('delete_email_parser_connection_button')}
        />
      </div>
    </Stack>
  )
}
